import React, { useState, useEffect, useContext } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { useAlert } from '../../context/AlertContext';
import SeatContext from '../../context/SeatContext';
import Sidedash from '../uicomponents/Sidedash'
import { SLOT_KEYS } from '../../utils/slotPricing';
import { LOCATION_COLOR_PALETTE, uniqueSeatLocations, colorForLocation } from '../../utils/locationColors';

const SLOT_LABELS = {
    morning: 'Morning (6 AM - 10 AM)',
    afternoon: 'Afternoon (10 AM - 2 PM)',
    evening: 'Evening (2 PM - 6 PM)',
    night: 'Night (6 PM - 10 PM)'
};

const Settings = () => {
    const { settings, loading, updateSettings } = useSettings();
    const { seats, getAllSeats } = useContext(SeatContext);
    const [formData, setFormData] = useState({
        productPrice: 400,
        slotPrices: {
            morning: 400,
            afternoon: 400,
            evening: 400,
            night: 400
        },
        autoApplyCoupons: {
            oneItem: { name: '', value: 0, enabled: false },
            twoItems: { name: '', value: 0, enabled: false },
            threeItems: { name: 'TRIPLEDEAL', value: 200, enabled: true },
            fourItems: { name: 'FOURBUNDLE', value: 300, enabled: true }
        },
        twilightCoupon: {
            name: 'TWILIGHT',
            value: 100,
            enabled: true
        },
        disableAfternoonEveningCoupon: false,
        hideBookSeatNowButton: false,
        hideRenewBookingButton: false,
        lockerSecurityDeposit: 0,
        localMonthlyCharge: 0,
        locationColors: {}
    });

    const { showAlert } = useAlert();
    const seatLocations = uniqueSeatLocations(seats);
    const seatLocationsKey = seatLocations.join('\0');

    useEffect(() => {
        const role = localStorage.getItem('role');
        if (localStorage.getItem('token') && (role === 'Admin' || role === 'Superadmin')) {
            getAllSeats();
        }
    }, [getAllSeats]);

    useEffect(() => {
        if (settings) {
            setFormData(prev => ({
                ...prev,
                ...settings,
                autoApplyCoupons: {
                    ...prev.autoApplyCoupons,
                    ...(settings.autoApplyCoupons || {})
                },
                twilightCoupon: {
                    ...prev.twilightCoupon,
                    ...(settings.twilightCoupon || {})
                },
                slotPrices: {
                    ...prev.slotPrices,
                    ...(settings.slotPrices || {})
                },
                locationColors: {
                    ...(settings.locationColors || {})
                }
            }));
        }
    }, [settings]);

    // Seed default palette colors for locations that don't have a saved choice yet
    useEffect(() => {
        if (!seatLocationsKey) return;
        const locations = seatLocationsKey.split('\0').filter(Boolean);
        setFormData(prev => {
            const next = { ...(prev.locationColors || {}) };
            let changed = false;
            locations.forEach((location, index) => {
                if (!next[location]) {
                    next[location] = LOCATION_COLOR_PALETTE[index % LOCATION_COLOR_PALETTE.length];
                    changed = true;
                }
            });
            if (!changed) return prev;
            return { ...prev, locationColors: next };
        });
    }, [seatLocationsKey]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCouponChange = (itemCount, field, value) => {
        setFormData(prev => ({
            ...prev,
            autoApplyCoupons: {
                ...prev.autoApplyCoupons,
                [itemCount]: {
                    ...prev.autoApplyCoupons[itemCount],
                    [field]: value
                }
            }
        }));
    };

    const handleTwilightCouponChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            twilightCoupon: {
                ...prev.twilightCoupon,
                [field]: value
            }
        }));
    };

    const handleSlotPriceChange = (slotKey, value) => {
        setFormData(prev => ({
            ...prev,
            slotPrices: {
                ...prev.slotPrices,
                [slotKey]: value
            }
        }));
    };

    const handleLocationColorChange = (location, color) => {
        setFormData(prev => ({
            ...prev,
            locationColors: {
                ...(prev.locationColors || {}),
                [location]: color
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const slotPrices = {};
        SLOT_KEYS.forEach((key) => {
            const raw = formData.slotPrices?.[key];
            const n = raw === '' || raw === undefined || raw === null ? NaN : Number(raw);
            slotPrices[key] = Number.isFinite(n) ? n : Number(formData.productPrice) || 400;
        });
        const success = await updateSettings({ ...formData, slotPrices });
        if (success) {
            showAlert('Settings updated successfully', 'success');
        } else {
            showAlert('Failed to update settings', 'danger');
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <Sidedash />
        <div className="col-md-9 pt-3">
            <div className="row">
                <div className="container mt-4">
                    <h2>System Settings</h2>
                    <form onSubmit={handleSubmit}>
                        <div className="card mb-4">
                            <div className="card-header">
                                <h4>Product Settings</h4>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="productPrice" className="form-label">Default seat price / fallback (₹)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="productPrice"
                                        name="productPrice"
                                        value={formData.productPrice}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                    <div className="form-text">Used when a slot has no price set, or no slot is selected on the receipt.</div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Price by time slot (₹)</label>
                                    <div className="row g-2">
                                        {SLOT_KEYS.map((key) => (
                                            <div key={key} className="col-md-6">
                                                <label htmlFor={`slotPrice-${key}`} className="form-label small text-muted mb-1">{SLOT_LABELS[key]}</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    id={`slotPrice-${key}`}
                                                    value={formData.slotPrices[key] ?? ''}
                                                    onChange={(e) => handleSlotPriceChange(key, e.target.value)}
                                                    min="0"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="lockerSecurityDeposit" className="form-label">Locker Security Deposit (₹)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="lockerSecurityDeposit"
                                        name="lockerSecurityDeposit"
                                        value={formData.lockerSecurityDeposit}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="localMonthlyCharge" className="form-label">Local Monthly Charge (₹)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="localMonthlyCharge"
                                        name="localMonthlyCharge"
                                        value={formData.localMonthlyCharge}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h4>Seat Location Filters &amp; Colors</h4>
                            </div>
                            <div className="card-body">
                                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                    These filters appear on Seat Management. Pick a color for each location from the palette.
                                </p>
                                {seatLocations.length === 0 ? (
                                    <div className="alert alert-info mb-0">
                                        No seat locations yet. Add seats with a Seat Location to manage filters here.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {seatLocations.map((location) => {
                                            const selected =
                                                formData.locationColors?.[location] ||
                                                colorForLocation(location, seatLocations, formData.locationColors).backgroundColor;
                                            return (
                                                <div
                                                    key={location}
                                                    className="d-flex flex-wrap align-items-center justify-content-between gap-2"
                                                    style={{
                                                        padding: '0.75rem 1rem',
                                                        borderRadius: '10px',
                                                        border: '1px solid #e9ecef',
                                                        backgroundColor: '#fafbfc'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span
                                                            aria-hidden="true"
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: 8,
                                                                backgroundColor: selected,
                                                                border: '1px solid rgba(0,0,0,0.1)',
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                        <div>
                                                            <div style={{ fontWeight: 600, color: '#2c3e50' }}>{location}</div>
                                                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Filter button on All Seats</div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2" role="group" aria-label={`Color for ${location}`}>
                                                        {LOCATION_COLOR_PALETTE.map((color) => {
                                                            const isSelected = selected === color;
                                                            return (
                                                                <button
                                                                    key={color}
                                                                    type="button"
                                                                    title={color}
                                                                    onClick={() => handleLocationColorChange(location, color)}
                                                                    style={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: color,
                                                                        border: isSelected ? '3px solid #2c3e50' : '2px solid #fff',
                                                                        boxShadow: isSelected
                                                                            ? '0 0 0 2px #2c3e50'
                                                                            : '0 0 0 1px rgba(0,0,0,0.15)',
                                                                        cursor: 'pointer',
                                                                        padding: 0
                                                                    }}
                                                                    aria-pressed={isSelected}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h4>Auto-Apply Coupons</h4>
                            </div>
                            <div className="card-body">
                                {['oneItem', 'twoItems', 'threeItems', 'fourItems'].map((itemCount) => (
                                    <div key={itemCount} className="mb-4">
                                        <h5>{itemCount === 'oneItem' ? '1 Item' :
                                            itemCount === 'twoItems' ? '2 Items' :
                                                itemCount === 'threeItems' ? '3 Items' : '4 Items'}</h5>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label">Coupon Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.autoApplyCoupons[itemCount].name}
                                                        onChange={(e) => handleCouponChange(itemCount, 'name', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label">Discount Value (₹)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.autoApplyCoupons[itemCount].value}
                                                        onChange={(e) => handleCouponChange(itemCount, 'value', parseFloat(e.target.value))}
                                                        min="0"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`enabled-${itemCount}`}
                                                            checked={formData.autoApplyCoupons[itemCount].enabled}
                                                            onChange={(e) => handleCouponChange(itemCount, 'enabled', e.target.checked)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`enabled-${itemCount}`}>
                                                            Enable
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h4>Twilight Coupon (Morning + Night)</h4>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Coupon Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.twilightCoupon.name}
                                                onChange={(e) => handleTwilightCouponChange('name', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Discount Value (₹)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.twilightCoupon.value}
                                                onChange={(e) => handleTwilightCouponChange('value', parseFloat(e.target.value))}
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="enabled-twilight"
                                                    checked={formData.twilightCoupon.enabled}
                                                    onChange={(e) => handleTwilightCouponChange('enabled', e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="enabled-twilight">
                                                    Enable
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h4>Coupon Restrictions</h4>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="disable-afternoon-evening"
                                            checked={formData.disableAfternoonEveningCoupon}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                disableAfternoonEveningCoupon: e.target.checked
                                            }))}
                                        />
                                        <label className="form-check-label" htmlFor="disable-afternoon-evening">
                                            Disable Auto-Apply Coupon for Afternoon + Evening Combination
                                        </label>
                                        <div className="form-text">
                                            When enabled, no auto-apply coupon will be applied when a user selects exactly 2 slots with afternoon + evening combination.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h4>Seat Booking Controls</h4>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="hide-book-seat-now-button"
                                            checked={!!formData.hideBookSeatNowButton}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                hideBookSeatNowButton: e.target.checked
                                            }))}
                                        />
                                        <label className="form-check-label" htmlFor="hide-book-seat-now-button">
                                            Hide "Book a Seat Now" button for users without seat booking
                                        </label>
                                    </div>
                                </div>

                                <div className="mb-0">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="hide-renew-booking-button"
                                            checked={!!formData.hideRenewBookingButton}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                hideRenewBookingButton: e.target.checked
                                            }))}
                                        />
                                        <label className="form-check-label" htmlFor="hide-renew-booking-button">
                                            Hide "Renew Booking" button for users with seat booking
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary">Save Settings</button>
                    </form>
                </div>
            </div>
        </div>
            </div>
        </div>
    );
};

export default Settings;
